/**
 * TPA balance display data resource route (SFCC-10811).
 *
 * Fetches balance from the SCAPI custom-API (`customers/balanceDisplayData`)
 * with the shopper's Bearer token. Returns null for guests and non-TPA users.
 * The underlying MuleSoft call can be slow — response is client-triggered
 * only; never block a page loader with this fetch.
 */
import { createContext, useContext, useEffect } from "react";

import { tryGetPresetContext } from "~/.server/preset-context.server";
import {
  buildAsiflexBalanceDisplay,
  fetchBalanceDisplayData,
  parseAsiflexCookie,
  withLiveBasketUsage,
} from "~/hec/.server/tpa-balance.server";
import type { BalanceDisplayData } from "~/hec/.server/tpa-balance.server";

import { BasketModel } from "~/commerce-api/models/basket";
import {
  getSitePreference,
  getSitePreferenceSafe,
} from "~/hec/.server/site-preferences";
import { type SiteId, bySiteId } from "~/hec/utils/site-id";
import type { LoaderArgs } from "~/lib/loadargs.types";
import { getOrderTotal } from "~/routes/checkout/utils/totals";

export interface TpaBalancePayload {
  balance: BalanceDisplayData | null;
  expiryDays: number;
  // Distinguishes a fetch failure (`error` — the component shows a message) from
  // a shopper with no balance (`empty` — renders nothing). SFCC-10536.
  status: "ok" | "empty" | "error";
}

// Sites that read balanceTrackerExpiryDays from Business Manager (mirrors composable ssrTpaHelper.ts).
const USES_EXPIRY_PREF_BY_SITE: Record<SiteId, boolean> = {
  FSASTORE: true,
  HSASTORE: false,
  WDH: false,
};

export const loader = async (args: LoaderArgs): Promise<TpaBalancePayload> => {
  // The client hook re-arms its fetch whenever the fetcher settles back to
  // idle with no data (SFCC-11803). A loader throw would leave `fetcher.data`
  // undefined and re-trigger the load — a retry loop against a failing
  // upstream. Catch everything and settle to a terminal `error` payload
  // instead; the component renders its unavailable message.
  try {
    return await loadBalancePayload(args);
  } catch (e) {
    console.warn(
      "[tpa-balance] loader failed; returning error payload",
      e instanceof Error ? e.message : e,
    );
    return { balance: null, expiryDays: 60, status: "error" };
  }
};

const loadBalancePayload = async ({
  request,
  context,
}: LoaderArgs): Promise<TpaBalancePayload> => {
  // Session-usage parity with SFRA: derive used/left-to-spend from the live
  // basket total (`getBalanceSpendingData`). `resolve` adopts the shopper's
  // real basket without creating one; a fetch failure resolves to `null` and
  // the caller keeps whatever usage it already has rather than failing the
  // whole balance bar. Only read for a balance that shows a tracker — a guest
  // or a non-TPA shopper never pays for the basket call.
  const getBasketTotal = () =>
    context.shop
      .getBasket("resolve")
      .then(b => (b ? getOrderTotal(new BasketModel(b)) : 0))
      .catch(() => null);

  // ASIFLEX path (SFCC-11421): balance comes from the `asiflexData` cookie, not
  // the SCAPI endpoint, and ASIFLEX shoppers are typically guests. Gate
  // SFRA-strict (enable pref + balance + consent) and short-circuit the Mulesoft
  // fetch. The pref read is wrapped — a cold-cache prefs blip must not 500 the
  // balance bar (mirrors getMagicLinkConfig).
  const asiflex = parseAsiflexCookie(request.headers.get("cookie"));
  if (asiflex?.consented) {
    const enabled = await getSitePreferenceSafe<boolean>(
      "ASIFlexBalanceTrackerEnabled",
      false,
    );
    if (enabled) {
      // The cookie balance is still valid without a basket, so a basket
      // failure falls back to 0 usage (raw allocated balance) and stays `ok`.
      const basketTotal = (await getBasketTotal()) ?? 0;
      return {
        balance: buildAsiflexBalanceDisplay(asiflex.balance, basketTotal),
        expiryDays: 60,
        status: "ok",
      };
    }
  }

  const siteId = tryGetPresetContext()?.preset.siteId;
  const usesExpiryPref = siteId
    ? bySiteId(siteId, USES_EXPIRY_PREF_BY_SITE)
    : false;
  // First fetch after login (provider-armed, COM-674): have SFRA re-pull the
  // balance + consent from MuleSoft instead of serving the stored copy.
  const refresh = new URL(request.url).searchParams.get("refresh") === "true";
  const [result, expiryDaysRaw] = await Promise.all([
    fetchBalanceDisplayData({ refresh }),
    usesExpiryPref
      ? getSitePreference<number>("balanceTrackerExpiryDays", 60)
      : Promise.resolve(60),
  ]);
  // Chained, not parallel: the basket read only runs when there is a tracker
  // to move (data-call-ledger COM-674).
  let balance: BalanceDisplayData | null = null;
  if (result.status === "ok") {
    balance = result.data.balance?.tracker
      ? withLiveBasketUsage(result.data, await getBasketTotal())
      : result.data;
  }
  return {
    balance,
    expiryDays: Number(expiryDaysRaw) || 60,
    status: result.status,
  };
};

export interface TpaBalanceContextValue {
  data: TpaBalancePayload | undefined;
  isLoading: boolean;
  /** Settled with nothing to show — a skeleton consumer must stop waiting. */
  failed: boolean;
  /** Opt in to the fetch (idempotent) — the balance is wanted by some shoppers only. */
  request: () => void;
  reload: () => void;
}

// Consumed by `TpaBalanceProvider` (./tpa-balance-provider). The provider lives
// in its own module because a component export here would push this context and
// the hook below out of the file instead
// (`react-refresh/only-export-components`) — and this is the side with all the
// callers, so it keeps the import path.
export const TpaBalanceContext = createContext<TpaBalanceContextValue | null>(
  null,
);

const noop = () => {};

/**
 * Read the page's single TPA-balance fetch, opting in when `enabled`.
 *
 * `TpaBalanceProvider` owns the request. This replaced a shared `"tpa-balance"`
 * fetcher key whose `load()` rejection was thrown to the router: the
 * layout/cart routes export no ErrorBoundary, so an abort — the browser
 * cancelling this >30s MuleSoft call when the shopper taps "Continue to
 * Checkout" — reached RootErrorBoundary and blanked the storefront
 * (SFCC-11869). Returns inert values with no provider mounted, so a component
 * rendered outside the layout tree degrades instead of throwing.
 */
export const useTpaBalance = (
  enabled = true,
): {
  data: TpaBalancePayload | null | undefined;
  isLoading: boolean;
  failed: boolean;
  reload: () => void;
} => {
  const ctx = useContext(TpaBalanceContext);
  const request = ctx?.request;

  useEffect(() => {
    if (enabled) request?.();
  }, [enabled, request]);

  return {
    data: ctx?.data ?? null,
    isLoading: ctx?.isLoading ?? false,
    failed: ctx?.failed ?? false,
    reload: ctx?.reload ?? noop,
  };
};
